import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useClient } from 'urql';
import { Modal } from '@/components/wireframe/ui';
import { useWorkspace } from '@/stores/workspace.store';
import { useUi } from '@/stores/ui.store';
import {
  Q_SOURCES,
  M_CREATE_SOURCE,
  M_TOGGLE_SOURCE,
  M_DELETE_SOURCE,
  M_REFRESH_SOURCE,
} from '@/lib/queries';

interface Source {
  id: string;
  projectId: string;
  kind: string;
  label: string;
  url: string | null;
  keyword: string | null;
  status: string;
  itemCount: number;
  lastFetchedAt: string | null;
  createdAt: string;
}

function humanizeDate(d: string | null): string {
  return d ? new Date(d).toLocaleDateString() : 'never';
}

/** Source kinds that the scrape worker actually understands (see workers/jobs/scrape.ts):
 *  - rss/atom feed (the default URL path), youtube channel (→ uploads feed),
 *  - keyword monitor (Google News search — no URL needed). No real HTML "scrape". */
type Needs = 'url' | 'keyword';
const SOURCE_KINDS: { id: string; label: string; needs: Needs; urlLabel?: string; urlPlaceholder?: string; help: string }[] = [
  { id: 'rss',     label: 'RSS / Atom feed', needs: 'url',     urlLabel: 'Feed URL',    urlPlaceholder: 'https://example.com/feed.xml',        help: 'Paste an RSS or Atom feed URL — we poll it for new items.' },
  { id: 'youtube', label: 'YouTube channel', needs: 'url',     urlLabel: 'Channel URL', urlPlaceholder: 'https://www.youtube.com/channel/UC…', help: 'A channel URL — we pull its uploads feed automatically.' },
  { id: 'keyword', label: 'Keyword monitor', needs: 'keyword',                                                                                  help: 'Tracks Google News for your search terms — no URL needed.' },
];

function normalizeUrl(u: string): string {
  const t = u.trim();
  if (!t) return '';
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}
function looksLikeUrl(u: string): boolean {
  try { const x = new URL(normalizeUrl(u)); return !!x.hostname && x.hostname.includes('.'); } catch { return false; }
}

export default function SourcesRoute() {
  const { toast } = useUi();
  const nav = useNavigate();
  const projectId = useWorkspace((s) => s.project_id);

  const client = useClient();
  const [sources, refetch] = useQuery({
    query: Q_SOURCES,
    variables: { projectId },
  });
  const [, createSource] = useMutation(M_CREATE_SOURCE);
  const [, toggleSource] = useMutation(M_TOGGLE_SOURCE);
  const [, deleteSource] = useMutation(M_DELETE_SOURCE);
  const [, refreshSource] = useMutation(M_REFRESH_SOURCE);

  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{ kind: string; label: string; url: string; keyword: string }>({
    kind: 'rss',
    label: '',
    url: '',
    keyword: '',
  });

  const reload = () => refetch({ requestPolicy: 'network-only' });

  const list: Source[] = sources.data?.contentSources ?? [];

  const kindDef = SOURCE_KINDS.find((k) => k.id === form.kind) ?? SOURCE_KINDS[0];
  const needsUrl = kindDef.needs === 'url';
  const needsKeyword = kindDef.needs === 'keyword';
  const urlValid = !needsUrl || looksLikeUrl(form.url);
  const keywordValid = !needsKeyword || form.keyword.trim().length > 0;
  const canSubmit = !!projectId && form.label.trim().length > 0 && urlValid && keywordValid && !saving;

  const fetchNow = async (id: string) => {
    // The fetch is async on the worker, so this is fire-and-poll: queue it, then
    // poll until the worker stamps a newer lastFetchedAt, and report the delta.
    const before = list.find((s) => s.id === id);
    const baseline = before?.lastFetchedAt ?? null;
    const baseCount = before?.itemCount ?? 0;

    setRefreshingId(id);
    const r = await refreshSource({ id });
    if (r.error) { setRefreshingId(null); toast(r.error.message, 'error'); return; }
    if (r.data?.refreshSource === false) { setRefreshingId(null); toast('Not allowed to refresh this source', 'error'); return; }
    toast('Fetching…');

    for (let i = 0; i < 8; i++) {
      await new Promise((res) => setTimeout(res, 1500));
      const res = await client.query(Q_SOURCES, { projectId }, { requestPolicy: 'network-only' }).toPromise();
      const after = (res.data?.contentSources as Source[] | undefined)?.find((s) => s.id === id);
      if (after && after.lastFetchedAt !== baseline) {
        const delta = after.itemCount - baseCount;
        setRefreshingId(null);
        toast(delta > 0 ? `Fetched — ${delta} new item${delta === 1 ? '' : 's'}` : 'Fetched — no new items', 'success');
        return;
      }
    }
    // Timed out waiting; the job may still be running/retrying with backoff.
    setRefreshingId(null);
    toast('Still fetching — refresh shortly to see results', 'warning');
  };

  const toggle = async (id: string) => {
    const r = await toggleSource({ id });
    if (r.error) { toast(r.error.message, 'error'); return; }
    reload();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this source? This cannot be undone.')) return;
    const r = await deleteSource({ id });
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast('Source deleted');
    reload();
  };

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    const r = await createSource({
      input: {
        projectId,
        kind: form.kind,
        label: form.label.trim(),
        // Only url-kinds carry a URL; keyword-kinds carry the search query.
        url: needsUrl ? normalizeUrl(form.url) : null,
        keyword: form.keyword.trim() || null,
      },
    });
    setSaving(false);
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast('Source added — first fetch runs shortly');
    setShowAdd(false);
    setForm({ kind: 'rss', label: '', url: '', keyword: '' });
    reload();
  };

  return (
    <div className="p-8 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-stone-900">Sources</h1>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium"
        >
          + Add source
        </button>
      </div>

      {sources.fetching ? (
        <div className="text-stone-500">Loading sources…</div>
      ) : list.length === 0 ? (
        <div className="border-2 border-dashed border-stone-200 rounded-xl p-12 text-center text-stone-400">
          No sources yet — add one to start pulling content
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {list.map((s) => {
            const busy = refreshingId === s.id;
            const active = s.status === 'active';
            return (
              <div
                key={s.id}
                className="bg-white border border-stone-200 rounded-xl p-5 hover:border-stone-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-stone-900">{s.label}</h3>
                    <p className="text-sm text-stone-500 truncate">
                      {s.kind}{s.url ? ` · ${s.url}` : ''}
                    </p>
                    {s.keyword && (
                      <p className="text-xs text-stone-400 mt-0.5">keyword: {s.keyword}</p>
                    )}
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold flex items-center gap-1 ${
                      active
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-stone-50 text-stone-600 border-stone-200'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-stone-400'}`} />
                    {active ? 'Active' : 'Paused'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-center mb-3">
                  <div>
                    <p className="text-[10px] text-stone-400 uppercase">Last fetch</p>
                    <p className="text-xs font-medium text-stone-700">
                      {busy ? 'fetching…' : humanizeDate(s.lastFetchedAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => nav('/app/content?source=' + s.id)}
                    className="hover:bg-stone-50 rounded-lg"
                  >
                    <p className="text-[10px] text-stone-400 uppercase">Items</p>
                    <p className="text-xs font-medium text-violet-600">{s.itemCount} →</p>
                  </button>
                </div>

                <div className="flex gap-2 pt-3 border-t border-stone-100">
                  <button
                    onClick={() => toggle(s.id)}
                    className="flex-1 px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 rounded-lg border border-stone-200"
                  >
                    {active ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    onClick={() => fetchNow(s.id)}
                    disabled={busy}
                    className="flex-1 px-3 py-2 text-sm text-violet-600 hover:bg-violet-50 rounded-lg font-medium flex items-center justify-center gap-1.5 disabled:opacity-60"
                  >
                    {busy ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                        Fetching
                      </>
                    ) : (
                      'Fetch now'
                    )}
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg border border-stone-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <Modal title="Add source" onClose={() => setShowAdd(false)}>
          <form onSubmit={submitAdd} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Kind</label>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
              >
                {SOURCE_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <p className="text-[11px] text-stone-400 mt-1">{kindDef.help}</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Label *</label>
              <input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="e.g. TechCrunch"
              />
            </div>

            {needsUrl && (
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">{kindDef.urlLabel} *</label>
                <input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  inputMode="url"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                    form.url && !urlValid ? 'border-red-300 focus:ring-red-400' : 'border-stone-300 focus:ring-violet-500'
                  }`}
                  placeholder={kindDef.urlPlaceholder}
                />
                {form.url && !urlValid && <p className="text-[11px] text-red-600 mt-1">Enter a valid URL (e.g. https://example.com/feed.xml).</p>}
              </div>
            )}

            {needsKeyword ? (
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Search terms *</label>
                <input
                  value={form.keyword}
                  onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="e.g. generative AI, B2B marketing"
                />
                <p className="text-[11px] text-stone-400 mt-1">We monitor Google News for these terms.</p>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Relevance keyword <span className="text-stone-400 font-normal">(optional)</span></label>
                <input
                  value={form.keyword}
                  onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="e.g. AI, automation"
                />
                <p className="text-[11px] text-stone-400 mt-1">Boosts ranking for matching items — it does not filter the feed.</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 text-sm text-stone-600 hover:bg-stone-50 rounded-lg border border-stone-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving…' : 'Add source'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
